import React, {
	useState,
	useEffect,
	useReducer,
	useCallback,
	useContext,
	useMemo,
} from "react";
import { toast } from "react-toastify";
import moment from "moment";
import { capitalize } from "lodash";

import AppPageTitle from "../../components/AppPageTitle";
import AppTableActionMenu from "../../components/AppTableActionMenu";
import apiV2 from "../../services/apiV2";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ScheduleModal from "../../components/ScheduleModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import openSocket from "../../services/socket-io";
import PermissionModal from "../../components/PermissionModal";
import { AuthContext } from "../../context/Auth/AuthContext";

const getUrlParam = (param) =>
	new URLSearchParams(window.location.search).get(param);

const PAGE_SIZE = 20;

const reducer = (state, action) => {
	if (action.type === "SET_SCHEDULES") {
		return action.payload;
	}

	if (action.type === "UPDATE_SCHEDULES") {
		const schedule = action.payload;
		const scheduleIndex = state.findIndex((s) => s.id === schedule.id);
		if (scheduleIndex !== -1) {
			state[scheduleIndex] = schedule;
			return [...state];
		}
		return [schedule, ...state];
	}

	if (action.type === "DELETE_SCHEDULE") {
		const scheduleId = action.payload;

		const scheduleIndex = state.findIndex((s) => s.id === scheduleId);
		if (scheduleIndex !== -1) {
			state.splice(scheduleIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

function IconSearch() {
	return (
		<svg className="size-3.5 text-default-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="11" cy="11" r="8" />
			<path d="m21 21-4.35-4.35" />
		</svg>
	);
}

function IconPlus({ className = "size-4 me-1 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</svg>
	);
}

function IconChevronLeft({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="15 18 9 12 15 6" />
		</svg>
	);
}

function IconChevronRight({ className = "size-4 shrink-0" }) {
	return (
		<svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="9 18 15 12 9 6" />
		</svg>
	);
}

function IconEdit() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
			<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
		</svg>
	);
}

function IconDelete() {
	return (
		<svg className="size-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polyline points="3 6 5 6 21 6" />
			<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
		</svg>
	);
}

function IconMore() {
	return (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<circle cx="12" cy="12" r="1" />
			<circle cx="19" cy="12" r="1" />
			<circle cx="5" cy="12" r="1" />
		</svg>
	);
}

function scheduleMessagePreview(schedule) {
	const body = String(schedule?.body || "").trim();
	if (body) return body;
	const templateId = String(schedule?.templateId || "").trim();
	if (templateId) {
		return i18n.t("schedules.templateLabel", { id: templateId });
	}
	return "—";
}

function ScheduleStatusBadge({ status }) {
	const normalized = String(status || "").toLowerCase();

	let classes =
		"bg-default-200 text-default-600 dark:bg-default-700 dark:text-default-300";
	if (
		normalized.includes("erro") ||
		normalized.includes("error") ||
		normalized.includes("fail")
	) {
		classes = "bg-danger/10 text-danger";
	} else if (
		normalized.includes("send") ||
		normalized.includes("enviad") ||
		normalized.includes("success")
	) {
		classes = "bg-success/10 text-success";
	} else if (
		normalized.includes("pend") ||
		normalized.includes("schedul") ||
		normalized.includes("agend")
	) {
		classes = "bg-warning/10 text-warning";
	}

	return (
		<span className={`inline-flex items-center rounded px-2.5 py-0.5 text-xs font-medium ${classes}`}>
			{status ? capitalize(String(status).toLowerCase()) : "—"}
		</span>
	);
}

function ScheduleRowMenu({ onEdit, onDelete, showEdit }) {
	return (
		<AppTableActionMenu
			triggerIcon={<IconMore />}
			triggerAriaLabel={i18n.t("schedules.table.actions")}
			items={[
				showEdit && {
					key: "edit",
					label: i18n.t("schedules.buttons.edit"),
					icon: <IconEdit />,
					onClick: onEdit,
				},
				{
					key: "delete",
					label: i18n.t("schedules.buttons.delete"),
					icon: <IconDelete />,
					tone: "danger",
					onClick: onDelete,
				},
			]}
		/>
	);
}

function Schedules() {
	const [loading, setLoading] = useState(false);
	const [pageNumber, setPageNumber] = useState(1);
	const [selectedSchedule, setSelectedSchedule] = useState(null);
	const [deletingSchedule, setDeletingSchedule] = useState(null);
	const [permissionOpen, setPermissionOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [schedules, dispatch] = useReducer(reducer, []);
	const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
	const [contactId, setContactId] = useState(+getUrlParam("contactId"));
	const { user } = useContext(AuthContext);

	const fetchSchedules = useCallback(async () => {
		try {
			const { data } = await apiV2.get("/schedules", {
				params: {
					companyId: user?.companyId,
				},
			});
			const schedulesPayload = Array.isArray(data)
				? data
				: data?.schedules || [];
			dispatch({ type: "SET_SCHEDULES", payload: schedulesPayload });
			setLoading(false);
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	}, [user?.companyId]);

	const handleOpenScheduleModalFromContactId = useCallback(() => {
		if (contactId) {
			handleOpenScheduleModal();
		}
	}, [contactId]);

	useEffect(() => {
		setPageNumber(1);
	}, [searchParam]);

	useEffect(() => {
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			fetchSchedules();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [fetchSchedules]);

	useEffect(() => {
		handleOpenScheduleModalFromContactId();
		const socket = openSocket({
			scope: "schedules",
			userId: user.id,
			component: "Schedules",
		});

		socket.on("schedule", (data) => {
			if (data.action === "update" || data.action === "create") {
				dispatch({ type: "UPDATE_SCHEDULES", payload: data.schedule });
			}

			if (data.action === "delete") {
				dispatch({ type: "DELETE_SCHEDULE", payload: +data.scheduleId });
			}
		});

		return () => {
			socket.disconnect();
		};
	}, [handleOpenScheduleModalFromContactId, user.id]);

	const filteredSchedules = useMemo(() => {
		const term = searchParam.toLowerCase().trim();
		if (!term) return schedules;

		return schedules.filter((schedule) => {
			const haystack = [
				schedule.contact?.name,
				schedule.contact?.number,
				schedule.connection?.name,
				scheduleMessagePreview(schedule),
				schedule.templateId,
				schedule.status,
				schedule.sendAt ? moment(schedule.sendAt).format("DD/MM/YYYY HH:mm:ss") : "",
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();

			return haystack.includes(term);
		});
	}, [schedules, searchParam]);

	const total = filteredSchedules.length;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const rangeStart = total ? (pageNumber - 1) * PAGE_SIZE + 1 : 0;
	const rangeEnd = Math.min(pageNumber * PAGE_SIZE, total);

	useEffect(() => {
		if (pageNumber > totalPages) {
			setPageNumber(totalPages);
		}
	}, [pageNumber, totalPages]);

	const visibleSchedules = useMemo(() => {
		const start = (pageNumber - 1) * PAGE_SIZE;
		return filteredSchedules.slice(start, start + PAGE_SIZE);
	}, [filteredSchedules, pageNumber]);

	const pageNumbers = () => {
		const pages = [];
		const max = 5;
		let from = Math.max(1, pageNumber - 2);
		let to = Math.min(totalPages, from + max - 1);
		from = Math.max(1, to - max + 1);
		for (let p = from; p <= to; p += 1) pages.push(p);
		return pages;
	};

	const cleanContact = () => {
		const url = new URL(window.location.href);
		url.searchParams.delete("contactId");
		window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
		setContactId(null);
	};

	const handleOpenScheduleModal = () => {
		setScheduleModalOpen(true);
	};

	const handleCloseScheduleModal = () => {
		setSelectedSchedule(null);
		setScheduleModalOpen(false);
		cleanContact();
	};

	const handleSearch = (event) => {
		setSearchParam(event.target.value.toLowerCase());
	};

	const handleEditSchedule = (scheduleId) => {
		setSelectedSchedule(scheduleId);
		setScheduleModalOpen(true);
	};

	const handleDeleteSchedule = async (scheduleId) => {
		try {
			await apiV2.delete(`/schedules/${scheduleId}`, {
				params: { companyId: user.companyId },
			});
			toast.success(i18n.t("schedules.toasts.deleted"));
		} catch (err) {
			toastError(err);
		}
		setDeletingSchedule(null);
		await fetchSchedules();
	};

	return (
		<div className="app-admin-page">
			<PermissionModal open={permissionOpen} onClose={setPermissionOpen} />
			<ConfirmationModal
				title={
					deletingSchedule &&
					`${i18n.t("schedules.confirmationModal.deleteTitle")}`
				}
				open={confirmModalOpen}
				onClose={setConfirmModalOpen}
				onConfirm={() => handleDeleteSchedule(deletingSchedule.id)}
			>
				{i18n.t("schedules.confirmationModal.deleteMessage")}
			</ConfirmationModal>

			<ScheduleModal
				open={scheduleModalOpen}
				onClose={handleCloseScheduleModal}
				reload={fetchSchedules}
				scheduleId={selectedSchedule}
				contactId={contactId || null}
			/>

			<AppPageTitle
				title={i18n.t("schedules.title")}
				subtitle={i18n.t("schedules.subtitle")}
			/>

			<div className="card">
				<div className="card-header">
					<h6 className="card-title m-0">{i18n.t("schedules.listTitle")}</h6>
					<button
						type="button"
						className="btn btn-sm shrink-0 bg-primary text-white"
						onClick={handleOpenScheduleModal}
					>
						<IconPlus />
						{i18n.t("schedules.buttons.add")}
					</button>
				</div>

				<div className="card-header flex-wrap gap-3">
					<div className="relative w-full md:max-w-sm">
						<input
							type="search"
							className="form-input form-input-sm w-full ps-9"
							placeholder={i18n.t("schedules.searchPlaceholder")}
							value={searchParam}
							onChange={handleSearch}
						/>
						<div className="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
							<IconSearch />
						</div>
					</div>
				</div>

				<div className="flex min-h-0 flex-1 flex-col">
					{loading && schedules.length === 0 ? (
						<div className="overflow-x-auto px-2 pt-2">
							<table className="min-w-full">
								<tbody>
									<TableRowSkeleton columns={5} />
								</tbody>
							</table>
						</div>
					) : !loading && visibleSchedules.length === 0 ? (
						<div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
							<p className="m-0 font-semibold text-default-800 dark:text-default-100">
								{i18n.t("schedules.empty")}
							</p>
							{searchParam && (
								<p className="m-0 text-sm text-default-500 dark:text-default-400">
									{i18n.t("schedules.emptyHint")}
								</p>
							)}
						</div>
					) : (
						<div className="flex min-h-0 flex-1 flex-col">
							<div className="app-table-scroll">
								<div className="inline-block min-w-full align-middle">
									<div className="overflow-visible">
										<table className="min-w-full divide-y divide-default-200 dark:divide-default-700">
											<thead className="bg-default-150 dark:bg-default-800/60">
												<tr className="text-sm font-normal text-default-700 dark:text-default-300">
													<th className="px-3.5 py-3 text-start">
														{i18n.t("schedules.table.contact")}
													</th>
													<th className="px-3.5 py-3 text-start">
														{i18n.t("schedules.table.body")}
													</th>
													<th className="px-3.5 py-3 text-start">
														{i18n.t("schedules.table.sendAt")}
													</th>
													<th className="px-3.5 py-3 text-start">
														{i18n.t("schedules.table.status")}
													</th>
													<th className="px-3.5 py-3 text-center">
														{i18n.t("schedules.table.actions")}
													</th>
												</tr>
											</thead>
											<tbody className="divide-y divide-default-200 dark:divide-default-700">
												{visibleSchedules.map((schedule) => (
													<tr
														key={schedule.id}
														className="text-sm text-default-800 dark:text-default-200"
													>
														<td className="px-3.5 py-3">
															<div className="min-w-[12rem]">
																<h6 className="mb-0 font-semibold text-default-800 dark:text-default-100">
																	{schedule.contact?.name ?? "—"}
																</h6>
																<p className="mb-0 mt-1 text-xs text-default-500 dark:text-default-400">
																	{schedule.contact?.number ||
																		schedule.connection?.name ||
																		"—"}
																</p>
															</div>
														</td>
														<td className="max-w-md px-3.5 py-3">
															<p
																className="mb-0 truncate text-default-700 dark:text-default-200"
																title={scheduleMessagePreview(schedule)}
															>
																{scheduleMessagePreview(schedule)}
															</p>
														</td>
														<td className="whitespace-nowrap px-3.5 py-3">
															{schedule.sendAt
																? moment(schedule.sendAt).format("DD/MM/YYYY HH:mm:ss")
																: "—"}
														</td>
														<td className="px-3.5 py-3">
															<ScheduleStatusBadge status={schedule.status} />
														</td>
														<td className="whitespace-nowrap px-3.5 py-3 text-center">
															<ScheduleRowMenu
																showEdit={user?.name === "admin"}
																onEdit={() => handleEditSchedule(schedule.id)}
																onDelete={() => {
																	user.profile === "admin"
																		? setConfirmModalOpen(true)
																		: setPermissionOpen(true);
																	setDeletingSchedule(schedule);
																}}
															/>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							</div>

							{total > 0 && (
								<div className="card-footer">
									<p className="m-0 text-sm text-default-500">
										{i18n.t("schedules.pagination.showing", {
											start: rangeStart,
											end: rangeEnd,
											total,
										})}
									</p>
									<nav
										className="flex flex-wrap items-center gap-2"
										aria-label={i18n.t("schedules.pagination.label")}
									>
										<button
											type="button"
											className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
											disabled={pageNumber <= 1 || loading}
											onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
										>
											<IconChevronLeft className="size-4 me-1 shrink-0" />
											{i18n.t("schedules.pagination.previous")}
										</button>
										{pageNumbers().map((p) => (
											<button
												key={p}
												type="button"
												className={`btn size-7.5 shrink-0 border ${
													p === pageNumber
														? "border-primary bg-primary text-white"
														: "border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary dark:border-default-600 dark:text-default-300"
												}`}
												disabled={loading}
												onClick={() => setPageNumber(p)}
											>
												{p}
											</button>
										))}
										<button
											type="button"
											className="btn btn-sm shrink-0 border border-default-200 bg-transparent text-default-600 hover:border-primary/10 hover:bg-primary/10 hover:text-primary disabled:cursor-not-allowed disabled:opacity-60 dark:border-default-600 dark:text-default-300"
											disabled={pageNumber >= totalPages || loading}
											onClick={() => setPageNumber((p) => p + 1)}
										>
											{i18n.t("schedules.pagination.next")}
											<IconChevronRight className="size-4 ms-1 shrink-0" />
										</button>
									</nav>
								</div>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export default Schedules;
